import { useEffect, useState } from "react";
import { Save, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { getApiErrorMessage } from "@/lib/apiClient";

import { useOrcamento, useDespesas, useOrcadoRealizado, useFinanceiroMutations } from "@/features/projetos/hooks/useFinanceiro";
import { CATEGORIA_CUSTO } from "@/features/projetos/types";

const fmt = (v: number) => Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const hojeISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
// despesas só nas categorias != Mão de obra (MO vem do timesheet)
const CATS_DESPESA = [1, 2, 3];

export default function ProjetoCustosTab({ projetoId }: { projetoId: number }) {
  const { data: orcamento = [] } = useOrcamento(projetoId);
  const { data: despesas = [] } = useDespesas(projetoId);
  const { data: painel } = useOrcadoRealizado(projetoId);
  const { salvarOrcamento, salvarDespesa, excluirDespesa } = useFinanceiroMutations(projetoId);

  // orçamento (4 categorias)
  const [valores, setValores] = useState<Record<number, string>>({ 0: "0", 1: "0", 2: "0", 3: "0" });
  useEffect(() => {
    const m: Record<number, string> = { 0: "0", 1: "0", 2: "0", 3: "0" };
    orcamento.forEach(o => { m[o.categoria] = String(o.valorOrcado); });
    setValores(m);
  }, [orcamento]);

  const salvarOrc = async () => {
    try {
      for (let cat = 0; cat < 4; cat++)
        await salvarOrcamento.mutateAsync({ categoria: cat, valorOrcado: Number(valores[cat]) || 0 });
      toast.success("Orçamento salvo.");
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar orçamento.")); }
  };

  // despesa editor
  const [editId, setEditId] = useState<number | null>(null);
  const [categoria, setCategoria] = useState("1");
  const [data, setData] = useState(hojeISO());
  const [valor, setValor] = useState("0");
  const [descricao, setDescricao] = useState("");
  const limpar = () => { setEditId(null); setCategoria("1"); setData(hojeISO()); setValor("0"); setDescricao(""); };

  const salvarDesp = async () => {
    if (Number(valor) <= 0) { toast.error("Informe o valor (> 0)."); return; }
    try {
      await salvarDespesa.mutateAsync({ despId: editId ?? undefined, req: { categoria: Number(categoria), dtLancamento: data, valor: Number(valor), descricao } });
      toast.success("Despesa salva."); limpar();
    } catch (e) { toast.error(getApiErrorMessage(e, "Erro ao salvar despesa.")); }
  };
  const removerDesp = async (id: number) => {
    try { await excluirDespesa.mutateAsync(id); toast.success("Despesa removida."); if (editId === id) limpar(); }
    catch (e) { toast.error(getApiErrorMessage(e, "Erro ao remover despesa.")); }
  };

  return (
    <div className="space-y-4">
      {/* Painel Orçado × Realizado */}
      <Card>
        <CardHeader><CardTitle className="text-base">Orçado × Realizado</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Categoria</TableHead>
              <TableHead className="text-right">Orçado</TableHead>
              <TableHead className="text-right">Realizado</TableHead>
              <TableHead className="text-right">Desvio</TableHead>
              <TableHead className="text-right">% Execução</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(painel?.linhas ?? []).map(ln => (
                <TableRow key={ln.categoria}>
                  <TableCell>{CATEGORIA_CUSTO[ln.categoria] ?? ln.categoria}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(ln.orcado)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(ln.realizado)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${ln.desvio > 0 ? "text-red-600" : "text-green-600"}`}>{fmt(ln.desvio)}</TableCell>
                  <TableCell className="text-right tabular-nums">{Number(ln.percentualExecucao).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
              {painel && (
                <TableRow className="font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(painel.totalOrcado)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(painel.totalRealizado)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${painel.totalDesvio > 0 ? "text-red-600" : "text-green-600"}`}>{fmt(painel.totalDesvio)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
          {painel && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Resumo titulo="Custo mão de obra" valor={fmt(painel.custoMaoDeObra)} />
              <Resumo titulo="Total faturado" valor={fmt(painel.totalFaturado)} />
              <Resumo titulo="Saldo a faturar" valor={fmt(painel.saldoAFaturar)} />
              <Resumo titulo="Margem realizada" valor={fmt(painel.margemRealizada)} destaque={painel.margemRealizada < 0} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orçamento por categoria */}
      <Card>
        <CardHeader><CardTitle className="text-base">Orçamento por categoria</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            {CATEGORIA_CUSTO.map((nome, cat) => (
              <div key={cat}>
                <Label>{nome}</Label>
                <Input type="number" step="0.01" value={valores[cat] ?? "0"}
                  onChange={e => setValores(v => ({ ...v, [cat]: e.target.value }))} />
              </div>
            ))}
          </div>
          <Button onClick={salvarOrc}><Save className="mr-2 h-4 w-4" />Salvar orçamento</Button>
        </CardContent>
      </Card>

      {/* Despesas */}
      <Card>
        <CardHeader><CardTitle className="text-base">{editId ? "Editar despesa" : "Despesas realizadas"}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[160px]">
              <Label>Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATS_DESPESA.map(cat => <SelectItem key={cat} value={String(cat)}>{CATEGORIA_CUSTO[cat]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input type="date" value={data} onChange={e => setData(e.target.value)} /></div>
            <div className="w-32"><Label>Valor</Label><Input type="number" step="0.01" value={valor} onChange={e => setValor(e.target.value)} /></div>
            <div className="min-w-[200px] flex-1"><Label>Descrição</Label><Input value={descricao} onChange={e => setDescricao(e.target.value)} maxLength={200} /></div>
            <Button onClick={salvarDesp}>{editId ? <><Save className="mr-2 h-4 w-4" />Atualizar</> : <><Plus className="mr-2 h-4 w-4" />Adicionar</>}</Button>
            {editId && <Button variant="outline" onClick={limpar}>Cancelar</Button>}
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Categoria</TableHead><TableHead>Descrição</TableHead>
              <TableHead className="text-right">Valor</TableHead><TableHead className="w-24 text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {despesas.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Nenhuma despesa.</TableCell></TableRow>
              ) : despesas.map(d => (
                <TableRow key={d.id}>
                  <TableCell>{fmtData(d.dtLancamento)}</TableCell>
                  <TableCell>{CATEGORIA_CUSTO[d.categoria] ?? d.categoria}</TableCell>
                  <TableCell className="max-w-[260px] truncate">{d.descricao}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(d.valor)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => { setEditId(d.id); setCategoria(String(d.categoria)); setData(d.dtLancamento.slice(0, 10)); setValor(String(d.valor)); setDescricao(d.descricao ?? ""); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => removerDesp(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Resumo({ titulo, valor, destaque }: { titulo: string; valor: string; destaque?: boolean }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div className={`text-lg font-semibold tabular-nums ${destaque ? "text-red-600" : ""}`}>{valor}</div>
    </div>
  );
}
